import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const BreathingExercise = () => {
    const { t } = useTranslation();
    const [isActive, setIsActive] = useState(false);
    const [phase, setPhase] = useState('inhale'); // 'inhale', 'hold', 'exhale', 'pause'
    const [timeLeft, setTimeLeft] = useState(4);
    const [cycle, setCycle] = useState(0);

    const phases = useMemo(() => ({
        inhale: { duration: 4, next: 'hold', text: t('breathing.inhale') || 'Inspiră adânc' },
        hold: { duration: 4, next: 'exhale', text: t('breathing.hold') || 'Menține aerul' },
        exhale: { duration: 4, next: 'pause', text: t('breathing.exhale') || 'Expiră lent' },
        pause: { duration: 4, next: 'inhale', text: t('breathing.pause') || 'Pauză și relaxează' }
    }), [t]);

    const handlePhaseTransition = useCallback(() => {
        const currentPhase = phases[phase];
        const nextPhase = currentPhase.next;
        
        setPhase(nextPhase);
        setTimeLeft(phases[nextPhase].duration);
        
        if (nextPhase === 'inhale') {
            setCycle(prevCycle => prevCycle + 1);
        }
    }, [phase, phases]);

    useEffect(() => {
        let interval = null;
        
        if (isActive && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft(prevTime => prevTime - 1);
            }, 1000);
        } else if (isActive && timeLeft === 0) {
            handlePhaseTransition();
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isActive, timeLeft, handlePhaseTransition]);

    const startExercise = () => {
        setIsActive(true);
        setPhase('inhale');
        setTimeLeft(4);
        setCycle(0);
    };

    const stopExercise = () => {
        setIsActive(false);
        setPhase('inhale');
        setTimeLeft(4);
        setCycle(0);
    };

    // Phase visual properties
    const getPhaseStyle = () => {
        switch (phase) {
            case 'inhale':
                return {
                    scale: 'scale-110',
                    ringColor: 'border-terracotta ring-terracotta/20',
                    bgColor: 'bg-gradient-to-br from-terracotta-100 to-amber-100 dark:from-stone-800 dark:to-stone-700',
                    textColor: 'text-terracotta dark:text-terracotta-light',
                    hint: 'Lasă aerul să pătrundă ușor în abdomen'
                };
            case 'hold':
                return {
                    scale: 'scale-110',
                    ringColor: 'border-amber-400 ring-amber-400/20',
                    bgColor: 'bg-gradient-to-br from-amber-100 to-stone-100 dark:from-stone-800 dark:to-stone-700',
                    textColor: 'text-amber-700 dark:text-amber-400',
                    hint: 'Păstrează calmul și liniștea interioară'
                };
            case 'exhale':
                return {
                    scale: 'scale-90',
                    ringColor: 'border-sage ring-sage/20',
                    bgColor: 'bg-gradient-to-br from-sage-100 to-stone-100 dark:from-stone-800 dark:to-stone-700',
                    textColor: 'text-sage-700 dark:text-sage-300',
                    hint: 'Eliberează orice tensiune acumulată'
                };
            case 'pause':
                return {
                    scale: 'scale-90',
                    ringColor: 'border-stone-300 ring-stone-300/20',
                    bgColor: 'bg-gradient-to-br from-stone-100 to-white dark:from-stone-800 dark:to-stone-800',
                    textColor: 'text-stone-600 dark:text-stone-300',
                    hint: 'Simte starea de relaxare și destindere'
                };
            default:
                return {
                    scale: 'scale-100',
                    ringColor: 'border-terracotta ring-terracotta/20',
                    bgColor: 'bg-white dark:bg-stone-800',
                    textColor: 'text-terracotta',
                    hint: 'Apasă butonul de pornire pentru a începe'
                };
        }
    };

    const phaseStyle = getPhaseStyle();

    return (
        <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800 relative overflow-hidden transition-colors duration-300">
            {/* Ambient Background Glow */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-terracotta/5 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-sage/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="text-center relative z-10">
                {/* Header info */}
                <div className="mb-8">
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-100 mb-2">
                        {t('breathing.title')}
                    </h3>
                    <p className="text-stone-600 dark:text-stone-400 text-sm sm:text-base max-w-md mx-auto">
                        {t('breathing.subtitle')}
                    </p>
                </div>
                
                {/* Animated Breathing Circle */}
                <div className="flex justify-center items-center my-8">
                    <div className="relative w-64 h-64 flex items-center justify-center">
                        {/* Outer pulsating aura */}
                        <div 
                            className={`absolute inset-0 rounded-full border-2 transition-all duration-1000 ease-in-out ${
                                isActive ? `${phaseStyle.scale} ${phaseStyle.ringColor} ring-8` : 'border-stone-200 dark:border-stone-700'
                            }`}
                        ></div>

                        {/* Middle circle with soft gradient */}
                        <div 
                            className={`w-52 h-52 rounded-full flex flex-col items-center justify-center p-6 shadow-inner transition-all duration-1000 ease-in-out ${
                                isActive ? `${phaseStyle.bgColor} ${phaseStyle.scale}` : 'bg-stone-50 dark:bg-stone-800'
                            }`}
                        >
                            {isActive ? (
                                <>
                                    <span className="text-4xl font-bold tracking-tight text-stone-900 dark:text-white mb-1">
                                        {timeLeft}s
                                    </span>
                                    <span className={`text-sm font-semibold tracking-wide uppercase ${phaseStyle.textColor}`}>
                                        {phases[phase].text}
                                    </span>
                                </>
                            ) : (
                                <div className="text-center">
                                    <svg className="w-10 h-10 text-terracotta/70 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wider block">
                                        Tehnica 4-4-4-4
                                    </span>
                                    <span className="text-sm font-semibold text-stone-700 dark:text-stone-300">
                                        Box Breathing
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Subtitle / Guidance hint */}
                <p className="text-sm font-medium text-stone-500 dark:text-stone-400 mb-8 min-h-[24px]">
                    {isActive ? phaseStyle.hint : 'O metodă recomandată pentru reducerea rapidă a ritmului cardiac și a anxietății.'}
                </p>

                {/* Cycle and Controls */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    {!isActive ? (
                        <button
                            onClick={startExercise}
                            className="px-8 py-3.5 bg-terracotta hover:bg-terracotta-dark text-white font-semibold rounded-full shadow-warm hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 text-sm sm:text-base flex items-center"
                        >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                            {t('breathing.start')}
                        </button>
                    ) : (
                        <div className="flex items-center gap-4">
                            <button
                                onClick={stopExercise}
                                className="px-7 py-3 bg-stone-200 dark:bg-stone-700 text-stone-800 dark:text-stone-200 font-semibold rounded-full hover:bg-stone-300 dark:hover:bg-stone-600 transition-colors text-sm flex items-center"
                            >
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                                </svg>
                                {t('breathing.stop')}
                            </button>
                            
                            {cycle > 0 && (
                                <span className="text-xs px-3.5 py-1.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 font-medium border border-stone-200 dark:border-stone-700">
                                    {t('breathing.cycles_completed')}: {cycle}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BreathingExercise;
